import { Box, Chip, CircularProgress, Tab, Tabs, Typography } from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import axios from "axios";
import { useState } from "react";
import { useSelector } from "react-redux";
import { Colors } from "../../Colors";
import { RootState, useAppDispatch } from "../../store";
import { fetchInsurances } from "../../slices/InsurancesSlice";
import { PrimaryButton } from "../../subcomponents/CustomButton";
import SnackBar from "../../subcomponents/SnackBar";
import SettingsLayout from "./SettingsLayout";
import AllPayers from "./AllPayers";
import InsuranceTypes from "./InsuranceTypes";

export default function Insurances() {
  const dispatch = useAppDispatch();
  const user = useSelector((state: RootState) => state.auth.user);

  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{
    text: string;
    severity: "success" | "error";
  } | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleSync = async () => {
    if (!user?.token) return;
    setSyncing(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/payers/initialize`,
        {},
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      dispatch(fetchInsurances(user.token));
      setRefreshKey((k) => k + 1);
      setSyncMessage({ text: "Payers synced successfully.", severity: "success" });
    } catch {
      setSyncMessage({ text: "Failed to sync payers. Please try again.", severity: "error" });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <SettingsLayout>
      <Box sx={{ pb: 8 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            mb: 1,
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h5">Insurances</Typography>
            <Chip
              label="Company-Wide"
              size="small"
              sx={{
                backgroundColor: `${Colors.info}20`,
                color: Colors.info,
                border: `1px solid ${Colors.info}40`,
                fontSize: "0.7rem",
                height: "20px",
              }}
            />
          </Box>

          <PrimaryButton
            size="small"
            startIcon={syncing ? <CircularProgress size={14} color="inherit" /> : <SyncIcon />}
            onClick={handleSync}
            disabled={syncing}
          >
            Sync from WebPT
          </PrimaryButton>
        </Box>

        <Typography
          variant="body2"
          sx={{
            color: Colors.info,
            fontSize: "0.813rem",
            mb: 2,
            maxWidth: "640px",
          }}
        >
          Browse the full list of payers synced from your EHR, toggle per-payer settings, and configure per-type rules.
        </Typography>

        {syncMessage && <SnackBar message={syncMessage.text} severity={syncMessage.severity} />}

        <Tabs
          value={selectedTab}
          onChange={(_, value) => setSelectedTab(value)}
          textColor="primary"
          indicatorColor="primary"
          aria-label="insurances tabs"
          sx={{
            mb: 3,
            minHeight: 36,
            borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
            "& .MuiTab-root": {
              textTransform: "none",
              minHeight: 36,
              fontSize: "0.875rem",
              color: Colors.info,
              "&.Mui-selected": { color: Colors.primary },
            },
          }}
        >
          <Tab label="All Payers" />
          <Tab label="Insurance Types" />
        </Tabs>

        {selectedTab === 0 && <AllPayers />}
        {selectedTab === 1 && <InsuranceTypes refreshKey={refreshKey} />}
      </Box>
    </SettingsLayout>
  );
}
